import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (effectiveEndDate: string) => void;
  userCreatedAt: string;
  effectiveEndDatePreview: string;
}

const REASONS: Array<{ key: string; label: string }> = [
  { key: 'too_expensive', label: 'Te duur' },
  { key: 'missing_features', label: 'Niet de juiste functies' },
  { key: 'wrong_fit', label: 'Werkt niet voor mijn type bedrijf' },
  { key: 'paused_business', label: 'Tijdelijk gestopt met ondernemen' },
  { key: 'switched_tool', label: 'Overgestapt naar een andere tool' },
  { key: 'other', label: 'Anders' },
];

function formatDateNL(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

export function CancelSubscriptionModal({ open, onClose, onSuccess, effectiveEndDatePreview }: Props) {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [reasonOther, setReasonOther] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const otherSelected = selectedReasons.includes('other');
  const canSubmit =
    confirmed &&
    selectedReasons.length > 0 &&
    (!otherSelected || reasonOther.trim().length > 0) &&
    !submitting;

  const toggleReason = (key: string) => {
    setSelectedReasons(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Niet ingelogd');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`;
      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          reasons: selectedReasons,
          reason_other: otherSelected ? reasonOther.trim() : '',
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Opzeggen mislukt');
      onSuccess(data.effective_end_date);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onbekende fout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-[#231F20] flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Abonnement opzeggen
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Je toegang stopt op {formatDateNL(effectiveEndDatePreview)}.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Waarom zeg je op? (één of meerdere)
            </p>
            <div className="space-y-2">
              {REASONS.map(r => (
                <label key={r.key} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(r.key)}
                    onChange={() => toggleReason(r.key)}
                    className="w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35]"
                  />
                  <span className="text-sm text-gray-700">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {otherSelected && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vertel ons meer
              </label>
              <textarea
                value={reasonOther}
                onChange={e => setReasonOther(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none text-sm"
                placeholder="Wat had je gehoopt te vinden?"
              />
            </div>
          )}

          <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35]"
            />
            <span className="text-sm text-gray-700">
              Ik begrijp dat mijn toegang tot dibbi stopt op <strong>{formatDateNL(effectiveEndDatePreview)}</strong>{' '}
              en dat ik tot die datum dibbi gewoon kan blijven gebruiken.
            </span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={submitting}
          >
            Annuleren
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? 'Bezig met opzeggen...' : 'Definitief opzeggen'}
          </button>
        </div>
      </div>
    </div>
  );
}
